import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { assets } from '../../data/assets';
import { VideoLightbox } from './VideoLightbox';
const PLAY_ICON_PATH = 'M30 16C30.0008 16.3395 29.9138 16.6735 29.7473 16.9694C29.5808 17.2653 29.3406 17.5132 29.05 17.6887L11.04 28.7062C10.7364 28.8922 10.3886 28.9937 10.0326 29.0003C9.67661 29.0069 9.32532 28.9183 9.015 28.7437C8.70764 28.5719 8.4516 28.3213 8.2732 28.0177C8.09481 27.714 8.00051 27.3684 8 27.0162V4.98375C8.00051 4.6316 8.09481 4.28595 8.2732 3.98234C8.4516 3.67872 8.70764 3.4281 9.015 3.25625C9.32532 3.08168 9.67661 2.99312 10.0326 2.99972C10.3886 3.00631 10.7364 3.10781 11.04 3.29375L29.05 14.3112C29.3406 14.4868 29.5808 14.7346 29.7473 15.0306C29.9138 15.3265 30.0008 15.6605 30 16Z';
export function Showreel() {
  const [open, setOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();

  // Scroll-in: the reel starts inset and rounded, and grows to full width as it rises into view.
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'center center']
  });
  const smooth = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4
  });
  const scale = useTransform(smooth, [0, 1], reduceMotion ? [1, 1] : [0.82, 1]);
  const radius = useTransform(smooth, [0, 1], reduceMotion ? [0, 0] : [24, 0]);

  // Play button glides toward the cursor while hovering the reel, then settles back to centre.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, {
    stiffness: 150,
    damping: 18,
    mass: 0.6
  });
  const y = useSpring(my, {
    stiffness: 150,
    damping: 18,
    mass: 0.6
  });
  const [hovering, setHovering] = useState(false);
  const handleMove = (e: MouseEvent<HTMLButtonElement>) => {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left - rect.width / 2) * 0.35);
    my.set((e.clientY - rect.top - rect.height / 2) * 0.35);
  };
  const handleLeave = () => {
    setHovering(false);
    mx.set(0);
    my.set(0);
  };
  const getOrigin = useCallback(() => {
    const rect = frameRef.current?.getBoundingClientRect();
    return rect ? {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    } : null;
  }, []);
  const close = useCallback(() => setOpen(false), []);
  return <section ref={sectionRef} aria-label="Showreel" className="overflow-hidden bg-white" data-id="element-172" data-mp="2lxsfxdr7aptm">
      <div className="mx-auto w-full max-w-[85rem]" data-id="element-173" data-mp="2gy60cxnwgxhn">
        <motion.button ref={frameRef} type="button" onClick={() => setOpen(true)} onMouseEnter={() => setHovering(true)} onMouseMove={handleMove} onMouseLeave={handleLeave} aria-label="Play showreel" style={{
        scale,
        borderRadius: radius
      }} className="relative flex aspect-[2/3] w-full items-center justify-center overflow-hidden bg-black will-change-transform md:aspect-square lg:aspect-video" data-id="element-174" data-mp="2gwuyx4hxg1tp">
          <video className="absolute inset-0 h-full w-full object-cover opacity-70" autoPlay muted loop playsInline poster={assets.showreelPoster} aria-hidden="true" data-id="element-175" data-mp="mkttmdns2dfh">
            <source src={assets.showreelVideo} type="video/webm" data-id="element-176" data-mp="3uzlknj0cizva" />
          </video>
          <motion.span style={{
          x,
          y
        }} animate={{
          scale: hovering ? 1.12 : 1
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="relative z-10 flex aspect-square w-1/4 items-center justify-center rounded-full bg-white/15 backdrop-blur-[20px] sm:w-1/5 sm:backdrop-blur-[30px] md:w-[16%] lg:w-[11%]" data-id="element-177" data-mp="3rhiso7em93v">
            <motion.svg viewBox="0 0 32 32" fill="none" className="w-[24%] text-white" aria-hidden="true" animate={{
            scale: hovering ? 1.15 : 1
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-178" data-mp="3h6vxvccztqx4">
              <path d={PLAY_ICON_PATH} fill="currentColor" data-id="element-179" data-mp="1wggunc7gbgx1" />
            </motion.svg>
          </motion.span>
        </motion.button>
      </div>
      <VideoLightbox open={open} youtubeId={assets.showreelYoutubeId} previewSrc={assets.showreelVideo} previewPoster={assets.showreelPoster} getOrigin={getOrigin} onClose={close} data-id="element-180" data-mp="1rogxtsmoh44o" />
    </section>;
}